<template>
    <PbChatLauncher :chat="supportChat" :suppressed="sdkUnwelcome" :deferred="inSupportLab" />
</template>

<script setup lang="ts">
/**
 * The one way support chat opens in hive.
 *
 * The facade and the hand-off live in `pb-chat-widget`; what hive adds is the
 * two answers only hive can give — which pages reserve the corner, and which
 * page owns the widget instead of this button.
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import PbChatLauncher from 'pb-chat-widget/PbChatLauncher.vue';
import { supportChat, useLabOwnsChat } from '~/composables/useChatWidget';

/**
 * Full-bleed pages whose corner is spoken for. Nothing chat-related belongs
 * here — not the facade, and not the SDK's own launcher once it takes over.
 */
const FULL_BLEED_ROUTES = new Set(['/buzz/risk-chart', '/buzz/radar']);

const route = useRoute();

/**
 * The lab drives the widget itself and shows it deliberately, so chat is
 * deferred there rather than suppressed — hiding the SDK too would blank the
 * very thing that page exists to exercise. Deferring also holds back the
 * restore: a customer socket standing up behind the lab's anonymous-session
 * gate would leave the page claiming to test a path it is no longer on.
 */
const inSupportLab = computed(
    () => route.path === '/support-lab' || route.path.startsWith('/support-lab/'),
);

/**
 * Where the SDK's own UI is unwelcome, not merely the facade.
 *
 * The lab counts: `/support-lab/public` states in its own docblock that it must
 * never show customer data, and a panel opened elsewhere and carried in by
 * client-side navigation would do exactly that. The harness re-claims the
 * widget through `useLabOwnsChat` when IT opens one.
 */
const labOwnsChat = useLabOwnsChat();
const sdkUnwelcome = computed(
    () => FULL_BLEED_ROUTES.has(route.path) || (inSupportLab.value && !labOwnsChat.value),
);
</script>
